// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import CommentModel from '@shared/models/comment';
import { formatDate } from '@shared/utils/common';
import { ROLES_LABELS } from '@shared/constants/user';
import Flex from '@shared/components/Flex';
import Avatar from '@shared/components/Avatar';
import Ellipsis from '@shared/components/Ellipsis';

export enum Mode {
  view,
  create,
}

const DATE_FORMAT = 'll HH:mm';

export interface CommentProps {
  className?: string;
  data: CommentModel;
  mode: Mode;
}

class Comment extends React.Component<CommentProps> {
  shouldComponentUpdate(prevProps: CommentProps) {
    return prevProps.data.body !== this.props.data.body;
  }

  private get viewModeContent() {
    const { data } = this.props;
    const classes = withStyles.getClasses(this.props);

    return (
      <div className={classes.view}>
        <Flex
          alignItems="center"
          wrap="nowrap"
        >
          <Avatar fullName={data.userFullName} />
          <Flex
            autoWidth={false}
            direction="column"
            classes={{ root: classes.user }}
          >
            <div className={classes.commentTitle}>
              <span className={classes.fullName}>{data.userFullName !== undefined ? data.userFullName : 'Unknown'}, </span>
              <span>{formatDate(data.createdDate, DATE_FORMAT)}</span>
            </div>
            {this.roles}
          </Flex>
        </Flex>
        <div className={classes.text}>{data.body}</div>
      </div>
    );
  }

  private get content() {
    const { mode } = this.props;
    const modesContent = {
      [Mode.view]: this.viewModeContent,
      [Mode.create]: null
    };

    return modesContent[mode];
  }

  private get roles() {
    const { data } = this.props;
    const classes = withStyles.getClasses(this.props);
    const roles = data.roles.map((role) => ROLES_LABELS[role]).join(', ');

    return (
      <Ellipsis
        maxWidth={300}
        text={roles}
        classes={{ root: classes.userRole }}
      />
    );
  }

  render() {
    const classes = withStyles.getClasses(this.props);

    return (
      <div className={classes.root}>
        {this.content}
      </div>
    );
  }
}

const CommentStyled = withStyles(
  Comment, 
  (theme, props) => ({
    "root": {
      overflow: 'hidden',
    },
    "view": {
      padding: theme.spacing(4),
      backgroundColor: theme.palette.colors.greyLight5,
      border: `1px solid ${theme.palette.colors.greyLight}`,
      borderRadius: 8,
    },
    "text": {
      fontSize: 14,
      fontWeight: 500,
      marginTop: theme.spacing(2),
      wordWrap: 'break-word',
    },
    "user": {
      width: '100%',
    },
    "userRole": {
      fontSize: 12,
    },
    "commentTitle": {
      fontSize: 12,
    },
    "fullName": {
      fontWeight: 500,
    }
  })
);

export default CommentStyled;